<template>
  <div class="min-h-screen bg-bg-primary text-text-primary transition-colors duration-300">
    <!-- Loading screen while auth state is being determined -->
    <div
      v-if="authStore.authLoading"
      class="min-h-screen flex items-center justify-center bg-bg-primary"
    >
      <div class="text-center">
        <div class="relative w-16 h-16 mx-auto mb-4">
          <div class="absolute inset-0 border-4 border-accent-primary/20 rounded-full"></div>
          <div class="absolute inset-0 border-4 border-accent-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
        <p class="text-text-primary font-semibold">Loading...</p>
        <p class="text-text-secondary text-sm mt-1">Please wait</p>
      </div>
    </div>

    <!-- Main app content -->
    <div v-else>
      <div v-if="attendanceStore.deliveryMessage" role="status" class="p-3 bg-bg-secondary text-text-primary border-b border-border-primary">
        Attendance delivery: {{ attendanceStore.deliveryMessage }}
        <button class="ml-3 text-accent-primary" @click="attendanceStore.loadTodaysAttendance({ silent: true })">Retry / refresh status</button>
      </div>
      <div v-if="pendingVideoCount" role="status" class="p-3 bg-bg-secondary border-b border-border-primary text-sm">
        {{ pendingVideoCount }} attendance video{{ pendingVideoCount === 1 ? '' : 's' }} waiting to upload.
        {{ videoQueueMessage || 'Temporary copies are deleted automatically after upload is confirmed. Unfinished uploads resume when you reopen.' }}
        <button class="ml-3 text-accent-primary" @click="resumeAttendanceVideos(authStore.user?.uid)">Retry uploads</button>
      </div>
      <router-view />
    </div>

    <PwaUpdateBanner />
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '@/stores/auth'
import { useAttendanceStore } from '@/stores/attendance'
import { useThemeStore } from '@/stores/theme'
import { useNotifications } from '@/composables/useNotifications'
import { useLeaveRequestAlerts } from '@/composables/useLeaveRequestAlerts'
import { pendingVideoCount, videoQueueMessage, resumeAttendanceVideos, refreshVideoQueueStatus, setVideoQueueUser } from '@/services/attendanceVideoQueue'
import PwaUpdateBanner from '@/components/common/PwaUpdateBanner.vue'

const router = useRouter()
const toast = useToast()
const authStore = useAuthStore()
const attendanceStore = useAttendanceStore()
const themeStore = useThemeStore()
const { listenForMessages, requestPermission } = useNotifications()
useLeaveRequestAlerts()

const refreshAttendance = () => {
  if (authStore.isAuthenticated && document.visibilityState === 'visible') {
    void attendanceStore.loadTodaysAttendance({ silent: true })
    void resumeAttendanceVideos(authStore.user?.uid)
  }
}
onMounted(() => {
  window.addEventListener('online', refreshAttendance)
  document.addEventListener('visibilitychange', refreshAttendance)
})
onUnmounted(() => {
  window.removeEventListener('online', refreshAttendance)
  document.removeEventListener('visibilitychange', refreshAttendance)
})
watch(() => authStore.user?.uid, uid => {
  setVideoQueueUser(uid)
  void resumeAttendanceVideos(uid)
  if (uid) refreshAttendance()
}, { immediate: true })
let videoRetryTimer
const onVideoQueueMessage = event => { if (event.data?.type === 'attendance-videos-updated') void refreshVideoQueueStatus() }
onMounted(() => {
  navigator.serviceWorker?.addEventListener('message', onVideoQueueMessage)
  videoRetryTimer = setInterval(() => {
    if (authStore.isAuthenticated && document.visibilityState === 'visible') void resumeAttendanceVideos(authStore.user?.uid)
  }, 30000)
})
onUnmounted(() => {
  clearInterval(videoRetryTimer)
  navigator.serviceWorker?.removeEventListener('message', onVideoQueueMessage)
})

let reminderInterval = null

const startReminderCheck = () => {
  if (reminderInterval) return

  const checkReminders = async () => {
    if (authStore.isAuthenticated) {
      // 1. Clock In Reminder
      const shouldAlertIn = await attendanceStore.checkClockInReminder()
      if (shouldAlertIn) {
        if (Notification.permission === 'granted') {
          const n = new Notification('Attendance Alert', {
            body: "You haven't clocked in yet today. Don't forget to clock in!",
            icon: '/pwa-192x192.png',
            tag: 'clock-in-reminder',
          })
          n.onclick = () => {
            window.focus()
            router.push('/attendance')
            attendanceStore.dismissClockInReminder()
            n.close()
          }
        }

        toast.info("REMINDER: You haven't clocked in yet today!", {
          timeout: 0,
          onClick: () => {
            router.push('/attendance')
            attendanceStore.dismissClockInReminder()
          },
          onClose: () => attendanceStore.dismissClockInReminder(),
        })
      }

      // 2. Clock Out Reminder
      const shouldAlertOut = await attendanceStore.checkClockOutReminder()
      if (shouldAlertOut) {
        if (Notification.permission === 'granted') {
          const n = new Notification('Attendance Alert', {
            body: "Your shift has ended. Don't forget to clock out!",
            icon: '/pwa-192x192.png',
            tag: 'clock-out-reminder',
          })
          n.onclick = () => {
            window.focus()
            router.push('/attendance')
            attendanceStore.dismissReminder()
            n.close()
          }
        }

        toast.warning('REMINDER: Your shift has ended. Don\'t forget to clock out!', {
          timeout: 0,
          onClick: () => {
            router.push('/attendance')
            attendanceStore.dismissReminder()
          },
          onClose: () => attendanceStore.dismissReminder(),
        })
      }
    }
  }

  // Check immediately
  checkReminders()

  // Then check every 15 minutes
  reminderInterval = setInterval(checkReminders, 15 * 60 * 1000)
}

const stopReminderCheck = () => {
  if (reminderInterval) {
    clearInterval(reminderInterval)
    reminderInterval = null
  }
}

watch(() => authStore.isAuthenticated, async (isAuth) => {
  if (isAuth) {
    startReminderCheck()
    
    // Automatically setup notifications if authenticated
    try {
      // Delay slightly to ensure everything is ready
      setTimeout(async () => {
        const result = await requestPermission()
        if (result) {
          // Success
        }
      }, 2000)
    } catch (err) {
      console.error('Failed to setup notifications:', err)
    }
  } else {
    stopReminderCheck()
  }
}, { immediate: true })

onUnmounted(() => {
  stopReminderCheck()
})

onMounted(() => {
  // Initialize theme system
  themeStore.initializeTheme()
  // Start listening for notifications in foreground
  listenForMessages()
})
</script>

<style>
/* Global theme styles */
body {
  background-color: var(--bg-primary);
  color: var(--text-primary);
  transition: background-color 0.3s ease, color 0.3s ease;
}

/* Scrollbar styling - theme aware */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: var(--bg-secondary);
}

::-webkit-scrollbar-thumb {
  background: var(--border-primary);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--border-secondary);
}
</style>
